import { ReactNode } from 'react';

interface IStateIllustrationProps {
  size?: number;
}

const IllustrationBase = ({
  children,
  size = 120,
  tone = 'neutral',
}: IStateIllustrationProps & {
  children: ReactNode;
  tone?: 'neutral' | 'error';
}) => {
  const isError = tone === 'error';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle
        cx="60"
        cy="60"
        r="53"
        fill={isError ? '#FFF4F4' : '#F6F9FC'}
        stroke={isError ? '#F3D4D4' : '#DDE5ED'}
      />
      {children}
    </svg>
  );
};

export const InitialStateIllustration = ({ size }: IStateIllustrationProps) => (
  <IllustrationBase size={size}>
    <circle cx="52" cy="53" r="17" stroke="#718096" strokeWidth="3" />
    <circle cx="52" cy="53" r="12.5" stroke="#A3B1C2" strokeWidth="1.5" />
    <path
      d="M64.5 65.5L78 79"
      stroke="#718096"
      strokeWidth="4"
      strokeLinecap="round"
    />
    <path
      d="M89 33V41M85 37H93"
      stroke="#B7C5D6"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M31 82V90M27 86H35"
      stroke="#B7C5D6"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="78" cy="29" r="2" fill="#C7D3E0" />
  </IllustrationBase>
);

export const LoadingStateIllustration = ({ size }: IStateIllustrationProps) => {
  const bars = [
    { transform: 'rotate(0 60 60)', opacity: 1 },
    { transform: 'rotate(45 60 60)', opacity: 0.88 },
    { transform: 'rotate(90 60 60)', opacity: 0.76 },
    { transform: 'rotate(135 60 60)', opacity: 0.64 },
    { transform: 'rotate(180 60 60)', opacity: 0.52 },
    { transform: 'rotate(225 60 60)', opacity: 0.4 },
    { transform: 'rotate(270 60 60)', opacity: 0.28 },
    { transform: 'rotate(315 60 60)', opacity: 0.16 },
  ];

  return (
    <IllustrationBase size={size}>
      <g className="content-state-spinner">
        {bars.map((bar) => (
          <path
            key={bar.transform}
            d="M60 27V37"
            stroke="#3B82DB"
            strokeWidth="6"
            strokeLinecap="round"
            transform={bar.transform}
            opacity={bar.opacity}
          />
        ))}
      </g>
    </IllustrationBase>
  );
};

export const EmptyStateIllustration = ({ size }: IStateIllustrationProps) => (
  <IllustrationBase size={size}>
    <path
      d="M34 55L42 43H78L86 55V76C86 78.2 84.2 80 82 80H38C35.8 80 34 78.2 34 76V55Z"
      stroke="#718096"
      strokeWidth="3"
      strokeLinejoin="round"
    />
    <path
      d="M34 56H48L53 63H67L72 56H86"
      stroke="#718096"
      strokeWidth="3"
      strokeLinejoin="round"
    />
    <path
      d="M60 29V36"
      stroke="#AAB8C8"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <path
      d="M47 33L51 38"
      stroke="#C0CBD7"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <path
      d="M73 33L69 38"
      stroke="#C0CBD7"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </IllustrationBase>
);

export const ErrorStateIllustration = ({ size }: IStateIllustrationProps) => (
  <IllustrationBase size={size} tone="error">
    <path
      d="M39 68C31.8 68 26 62.2 26 55C26 48.2 31.2 42.6 37.9 42.1C40.9 34.9 48 30 56.2 30C67 30 75.8 38.6 76.1 49.3C83 50 88.4 55.9 88.4 63C88.4 70.6 82.2 76.8 74.6 76.8H42"
      stroke="#C44242"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="58"
      cy="70"
      r="15"
      fill="#FFF7F7"
      stroke="#C44242"
      strokeWidth="3"
    />
    <path
      d="M52 64L64 76M64 64L52 76"
      stroke="#C44242"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <path
      d="M28 31V37M25 34H31"
      stroke="#E5A9A9"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="84" cy="36" r="2" fill="#EDC0C0" />
    <circle cx="32" cy="88" r="2" fill="#EDC0C0" />
  </IllustrationBase>
);
